<template>
  <div>
    <b-row class="mb-1">
      <b-col
        cols="12"
        class="mb-1 d-flex align-items-center justify-content-end"
      >
        <div class="d-flex align-items-center" style="gap: 8px">
          <span class="text-nowrap">Rows per page</span>
          <b-form-select
            v-model="limit"
            :options="limitOptions"
            size="sm"
            style="width: auto"
          />
          <span class="text-nowrap text-muted">{{ paginationSummary }}</span>
        </div>
      </b-col>
      <!-- Date preset -->
      <b-col cols="4" md="2">
        <b-form-select
          v-model="datePreset"
          :options="datePresetOptions"
          @change="onDatePresetChange"
        />
        <flat-pickr
          v-if="datePreset === 'custom'"
          v-model="filters.rangeTime"
          class="form-control mt-1"
          :config="config"
          placeholder="From - To"
        />
      </b-col>

      <!-- Status -->
      <b-col cols="4" md="2">
        <b-dropdown
          id="status-filter"
          :text="titleStatus"
          variant="outline-dark"
          menu-class="p-1 w-100"
          class="w-100"
        >
          <p
            class="text-danger"
            style="
              text-decoration: underline;
              cursor: pointer;
              margin-bottom: 8px;
            "
            @click="clearFilter"
          >
            Clear Filter
          </p>
          <div
            v-for="opt in statusOptions"
            :key="opt.value"
            style="padding: 4px 0"
          >
            <b-form-checkbox :value="opt.value" v-model="filters.status">
              <span class="text-nowrap">{{ opt.label }}</span>
            </b-form-checkbox>
          </div>
        </b-dropdown>
      </b-col>

      <!-- Bet Type -->
      <b-col cols="4" md="2">
        <b-form-select
          v-model="filters.betType"
          :options="betTypeOptions"
          class="w-100"
        />
      </b-col>

      <!-- Stake Amount -->
      <b-col cols="4" md="2">
        <b-form-input
          v-model="filters.minStake"
          type="number"
          placeholder="Stake Amount (min)"
          class="w-100"
        />
      </b-col>

      <!-- Liability Amount -->
      <b-col cols="4" md="2">
        <b-form-input
          v-model="filters.minLiability"
          type="number"
          placeholder="Liability Amount (min)"
          class="w-100"
        />
      </b-col>

      <!-- Search -->
      <b-col cols="4" md="2">
        <b-form-input
          v-model="search"
          placeholder="Search by name or email address"
          class="w-100"
        />
      </b-col>

      <!-- Apply -->
      <b-col cols="4" md="2">
        <b-button
          variant="primary"
          class="mt-1 w-100 text-nowrap"
          @click="applyFilters"
        >
          <feather-icon icon="FilterIcon" />
          Apply filters
        </b-button>
      </b-col>

      <!-- Clear -->
      <b-col cols="4" md="2">
        <b-button
          variant="light"
          class="mt-1 w-100 text-nowrap"
          @click="clearFilters"
        >
          <feather-icon icon="XIcon" />
          Clear filter
        </b-button>
      </b-col>

      <!-- Export -->
      <b-col cols="4" md="2">
        <b-button
          variant="success"
          class="mt-1 w-100 text-nowrap"
          :disabled="exportingCSV"
          @click="exportCSV"
        >
          <b-spinner v-if="exportingCSV" small  />
          <feather-icon v-else icon="DownloadIcon" />
          {{ exportingCSV ? "Exporting..." : "Export CSV" }}
        </b-button>
      </b-col>
    </b-row>

    <div>
      <b-table
        :items="betslips"
        :fields="fields"
        responsive
        thead-class="text-nowrap"
        :busy="loadingInitial"
        show-empty
      >
        <template #cell(date)="{ item }">
          <div class="">
            <p class="text-nowrap">
              {{ format(new Date(item.createdTime), "dd/MM/yyyy hh:mm:ss") }}
            </p>
          </div>
        </template>

        <template #cell(id)="{ item }">
          <div>
            <p class="text-nowrap">{{ item.displayId }}</p>
          </div>
        </template>

        <template #cell(codeSport)="{ item }">
          <div style="width: 150px">{{ convertSportDescriptive(item) }}</div>
        </template>

        <template #cell(eventItem)="{ item }">
          <div style="min-width: 150px">
            <template v-if="item.type === 'multi'">
              Multi ({{ item.selections ? item.selections.length : 0 }})
            </template>
            <template v-else>{{ convertEvent(item) }}</template>
          </div>
        </template>

        <template #cell(selection)="{ item }">
          <!-- MULTI -->
          <template v-if="item.type === 'multi'">
            <div v-if="item.selections && item.selections.length > 0">
              <div v-for="(selection, idx) in item.selections" :key="idx">
                <template v-if="selection.hasOwnProperty('meetingId')">
                  <b-media class="text-nowrap">
                    {{ selection.runnerNo }}. {{ selection.runnerName }} ({{
                      selection.barrierPosition
                    }})
                    {{ idx < item.selections.length - 1 ? "," : "" }}</b-media
                  >
                </template>
                <template v-else>
                  <span class="text-muted">{{ selection.name }}</span>
                </template>
              </div>
            </div>
            <b-media v-else> - </b-media>
          </template>

          <!-- SINGLE -->
          <template v-else-if="item.type === 'single'">
            <template v-if="item.selections && item.selections.length > 0">
              <template v-if="item.selections[0].hasOwnProperty('meetingId')">
                <b-media class="text-nowrap">
                  {{ item.selections[0].runnerNo }}.
                  {{ item.selections[0].runnerName }} ({{
                    item.selections[0].barrierPosition
                  }})</b-media
                >
              </template>
              <template v-else>
                <span
                  class="text-muted"
                  v-if="item.selections && item.selections.length > 0"
                  >{{ item.selections[0].name }}</span
                >
                <b-media v-else> - </b-media>
              </template>
            </template>
            <b-media v-else> - </b-media>
          </template>

          <!-- SRM / EXOTIC -->
          <template v-else>
            <template v-if="item.selections && item.selections.length > 0">
              <template v-if="isExoticBetType(item.type)">
                <b-media class="text-nowrap">
                  {{ convertListToGroupedString(item.selections) }}
                </b-media>
              </template>
              <div v-else>
                <div v-for="(selection, idx) in item.selections" :key="idx">
                  <div v-if="selection.hasOwnProperty('meetingId')">
                    <b-media class="text-nowrap">
                      {{ selection.runnerNo }}. {{ selection.runnerName }} ({{
                        selection.barrierPosition
                      }})</b-media
                    >
                  </div>
                  <span v-else class="text-muted">{{ selection.name }}</span>
                </div>
              </div>
            </template>
          </template>
        </template>

        <template #cell(customer)="{ item }">
          <div>
            <div class="font-weight-bold text-nowrap">
              {{ item.name || item.username }}
            </div>
            <small class="text-muted">{{ item.email || "-" }}</small>
          </div>
        </template>

        <template #cell(betType)="{ item }">
          <template v-if="item.type === 'multi'">
            Multi ({{ item.selections ? item.selections.length : 0 }})
          </template>
          <template v-else>{{ item.type }}</template>
        </template>

        <template #cell(promo)="{ item }">
          <b-media class="text-nowrap">
            <b-badge
              v-if="
                item.currencyMethod === 'bonus' ||
                (item.promotions && item.promotions.length > 0)
              "
              :variant="item.currencyMethod === 'bonus' ? 'primary' : 'success'"
              >{{
                item.currencyMethod !== "bonus" &&
                item.promotions &&
                item.promotions.length > 0
                  ? "Boost"
                  : item.currencyMethod
              }}</b-badge
            >
          </b-media>
        </template>

        <template #cell(state)="{ item }">
          <div style="width: 150px">{{ convertState(item) }}</div>
        </template>

        <template #cell(marketType)="{ item }">
          <template v-if="item.type === 'single'">
            <b-media
              class="text-nowrap"
              v-if="item.selections && item.selections.length > 0"
              >{{
                item.selections[0].selectionType ||
                item.selections[0].marketType ||
                "-"
              }}</b-media
            >
            <b-media v-else> - </b-media>
          </template>
          <template v-else>
            <b-media>{{ item.type }}</b-media>
          </template>
        </template>

        <template #cell(walletUsed)="{ item }">
          <b-media class="text-nowrap text-capitalize">{{
            item.currencyMethod
          }}</b-media>
        </template>

        <template #cell(stake)="{ item }">
          <b-media class="text-nowrap">{{
            formatCurrencyBuiltInFunc(item.stake)
          }}</b-media>
        </template>
        <template #cell(liability)="{ item }">
          <b-media class="text-nowrap">{{
           !!item.odds && !!item.stake
                    ? formatCurrencyBuiltInFunc(item.stake * item.odds - item.stake)
                    : "TBD"
          }}</b-media>
        </template>
        <template #cell(result)="{ item }">
          <b-badge :variant="resultVariant(item.result)">{{
            item.result || "Unresulted"
          }}</b-badge>
        </template>
        <template #cell(actions)="data">
          <b-button
            size="sm"
            variant="outline-primary"
            @click="openModalInfoBet(data.item)"
            >Manage</b-button
          >
        </template>
      </b-table>
      <!-- Scroll sentinel for infinite scroll -->
      <div ref="scrollSentinel"></div>

      <!-- Loading more spinner -->
      <div
        v-if="loading && betslips && betslips.length > 0"
        class="text-center py-2"
      >
        <b-spinner small class="mr-1" />
        Loading more...
      </div>

      <!-- End of results -->
      <div
        v-if="!hasMore && betslips && betslips.length > 0"
        class="text-center text-muted small py-2"
      >
        All {{ betslips.length }} results loaded
      </div>
    </div>

    <ManageBetslip :selectedBet="selectedBet" @refresh="fetchBets" />
  </div>
</template>

<script>
import {
  BButton,
  BFormInput,
  BFormSelect,
  BBadge,
  BTable,
  BDropdown,
  BCol,
  BRow,
  BFormCheckbox,
  BMedia,
  BSpinner,
} from "bootstrap-vue";
import { formatCurrencyBuiltInFunc } from "@/helpers/utils";
import flatPickr from "vue-flatpickr-component";
import { debounce } from "debounce";
import { mapState, mapActions } from "vuex";
import ManageBetslip from "@/views/apps/trading/ManageBetslipV2.vue";
import {
  format,
  startOfDay,
  endOfDay,
  startOfWeek,
  startOfMonth,
  subDays,
} from "date-fns";

export default {
  components: {
    BButton,
    BFormInput,
    BFormSelect,
    BBadge,
    BTable,
    BSpinner,
    flatPickr,
    BDropdown,
    BCol,
    BRow,
    BFormCheckbox,
    BMedia,
    ManageBetslip,
  },
  data() {
    const betTypes = [
      {
        value: null,
        text: "All",
      },
      {
        value: "win",
        text: "Win",
      },
      {
        value: "place",
        text: "Place",
      },
      {
        value: "exotic",
        text: "Exotic",
      },
    ];
    const statusOptions = [
      { label: "Resulted Win", value: "resulted-win" },
      {
        label: "Resulted Loss",
        value: "resulted-loss",
      },
      { label: "Cancelled", value: "bet-cancelled" },
      { label: "Unresulted", value: "bet-pending" },
      { label: "Rejected", value: "rejected" },

      {
        label: "Fully Refunded",
        value: "bet-refunded",
      },
    ];
    const betTypeOptions = [
      { value: null, text: "All Bet Types" },
      { value: "single", text: "Single" },
      { value: "multi", text: "Multi" },
      { value: "quinella", text: "Quinella" },
      { value: "exacta", text: "Exacta" },
      { value: "trifecta", text: "Trifecta" },
      { value: "first4", text: "First 4" },
      { value: "daily-double", text: "Daily Double" },
      { value: "running-double", text: "Running Double" },
      { value: "treble", text: "Treble" },
      // { value: "srm", text: "SRM" },
      { value: "sgm", text: "SGM" },
      { value: "quaddie", text: "Quaddie" },
      { value: "combo-racing", text: "Combo Racing" },
    ];
    return {
      filters: {
        status: [],
        betType: null,
        minStake: null,
        minLiability: null,
        rangeTime: "",
      },
      betTypeOptions,
      search: "",
      titleStatus: "All Bet Statuses",
      statusOptions,
      limit: 100,
      limitOptions: [10, 25, 100, 500],
      betTypes,
      selectedBet: null,
      exportingCSV: false,
      scrollObserver: null,
      datePreset: "today",
      datePresetOptions: [
        { value: "today", text: "Today" },
        { value: "this-week", text: "This Week" },
        { value: "this-month", text: "This Month" },
        { value: "last-60-days", text: "Last 60 Days" },
        { value: "custom", text: "Custom Range" },
      ],
      fields: [
        { key: "date", label: "Date/Time" },
        { key: "id", label: "Bet ID" },
        { key: "customer", label: "Customer" },
        { key: "betType", label: "Bet Type" },
        { key: "promo", label: "Promo" },
        { key: "codeSport", label: "Code/Sport" },
        { key: "state", label: "State" },
        { key: "eventItem", label: "Event" },
        { key: "selection", label: "Selection", thStyle: "min-width: 220px" },
        { key: "marketType", label: "Market Type" },
        { key: "walletUsed", label: "Wallet Used" },
        { key: "stake", label: "Stake" },
        { key: "liability", label: "Liability" },
        { key: "result", label: "Bet Status" },
        { key: "actions", label: "Action" },
      ],
      config: {
        mode: "range",
        altFormat: "d-m-Y",
        altInput: true,
      },
    };
  },
  computed: {
    ...mapState("trading/list", {
      loading: "loadingBetslips",
      betslips: "betslips",
      hasMore: "hasMore",
    }),
    loadingInitial() {
      return this.loading && this.betslips.length === 0;
    },
    computedDateRange() {
      const now = new Date();
      switch (this.datePreset) {
        case "today":
          return { start: startOfDay(now), end: now };
        case "this-week":
          return { start: startOfWeek(now, { weekStartsOn: 1 }), end: now };
        case "this-month":
          return { start: startOfMonth(now), end: now };
        case "last-60-days":
          return { start: startOfDay(subDays(now, 59)), end: now };
        case "custom":
          if (this.filters.rangeTime) {
            const [startDate, endDate] =
              this.filters.rangeTime.split(" to ") || [];
            if (startDate && endDate) {
              return {
                start: startOfDay(new Date(startDate)),
                end: endOfDay(new Date(endDate)),
              };
            }
          }
          return null;
        default:
          return null;
      }
    },
    paginationSummary() {
      if (!this.betslips || this.betslips.length === 0) return "No results";
      return `${this.betslips.length} results loaded`;
    },
  },
  watch: {
    "$route.query.raceId": {
      immediate: true,
      handler() {
        this.fetchBets();
      },
    },
    "filters.betType"() {
      this.fetchBets();
    },
    search() {
      this.handleSearchDebounced();
    },
    "filters.status"(newStatus) {
      switch (newStatus.length) {
        case 0:
          this.titleStatus = "All Bet Statuses";
          break;
        case 1:
          const opt = this.statusOptions.find((o) => o.value === newStatus[0]);
          this.titleStatus = opt ? opt.label : "All Statuses";
          break;
        default:
          this.titleStatus = "Multi";
      }
    },
    limit() {
      this.fetchBets();
    },
  },
  mounted() {
    this.handleSearchDebounced = debounce(this.handleSearch, 300);
    this.$nextTick(() => {
      this.setupScrollObserver();
    });
  },
  beforeDestroy() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
  },
  methods: {
    ...mapActions({
      asyncBetslips: "trading/list/getBetslips",
      asyncLoadMoreBetslips: "trading/list/loadMoreBetslips",
      getAllBetslipsForExport: "trading/list/getAllBetslipsForExport",
    }),
    format,
    formatCurrencyBuiltInFunc,
    buildFilterParams() {
      const raceId = this.$route.query.raceId;
      const params = {
        limit: this.limit,
        search: this.search || undefined,
        type: this.filters.betType || undefined,
        minStake: this.filters.minStake || undefined,
        minLiability: this.filters.minLiability || undefined,
        raceId,
      };
      const dateRange = this.computedDateRange;
      if (dateRange) {
        params["createdTime[startDate]"] = dateRange.start.toISOString();
        params["createdTime[endDate]"] = dateRange.end.toISOString();
      }
      if (this.filters.status.length) {
        this.filters.status.forEach((status, index) => {
          params[`results[${index}]`] = status;
        });
      }
      return params;
    },
    setupScrollObserver() {
      if (!this.$refs.scrollSentinel) return;
      this.scrollObserver = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && this.hasMore && !this.loading) {
            this.loadMore();
          }
        },
        { threshold: 0.1, rootMargin: "200px" }
      );
      this.scrollObserver.observe(this.$refs.scrollSentinel);
    },
    async fetchBets() {
      const raceId = this.$route.query.raceId;
      if (this.loading || !raceId) return;
      try {
        await this.asyncBetslips(this.buildFilterParams());
      } catch (error) {
        // error handled in store
      }
    },
    async loadMore() {
      if (this.loading || !this.hasMore) return;
      try {
        await this.asyncLoadMoreBetslips(this.buildFilterParams());
      } catch (error) {
        // error handled in store
      }
    },
    displayWithCap(value, cap) {
      if (value == null) return "-";
      if (value > cap) return `> ${cap}`;
      return this.formatCurrencyBuiltInFunc(value);
    },
    handleSearch() {
      this.fetchBets();
    },
    onDatePresetChange() {
      if (this.datePreset !== "custom") {
        this.filters.rangeTime = "";
      }
    },
    openModalInfoBet(bet) {
      this.selectedBet = bet;
      this.$bvModal.show("bet-info");
    },
    applyFilters() {
      this.fetchBets();
    },
    clearFilter() {
      this.filters.status = [];
    },
    clearFilters() {
      this.datePreset = "today";
      this.filters.status = [];
      this.filters.betType = null;
      this.filters.minStake = null;
      this.filters.minLiability = null;
      this.filters.rangeTime = "";
      this.fetchBets();
    },
    convertSelectionText(item) {
      if (!item.selections || item.selections.length === 0) return "";
      if (item.type === "multi") {
        return item.selections
          .map((s) =>
            s.hasOwnProperty("meetingId")
              ? `${s.runnerNo}. ${s.runnerName} (${s.barrierPosition})`
              : s.name || ""
          )
          .join(", ");
      }
      if (item.type === "single") {
        const s = item.selections[0];
        if (s.hasOwnProperty("meetingId"))
          return `${s.runnerNo}. ${s.runnerName} (${s.barrierPosition})`;
        return s.name || "";
      }
      if (this.isExoticBetType(item.type)) {
        return this.convertListToGroupedString(item.selections);
      }
      return item.selections
        .map((s) =>
          s.hasOwnProperty("meetingId")
            ? `${s.runnerNo}. ${s.runnerName} (${s.barrierPosition})`
            : s.name || ""
        )
        .join(", ");
    },
    async fetchAllForExport() {
      const params = { ...this.buildFilterParams() };
      delete params.limit;
      return this.getAllBetslipsForExport(params);
    },
    async exportCSV() {
      if (this.exportingCSV) return;
      this.exportingCSV = true;
      let items;
      try {
        items = await this.fetchAllForExport();
      } catch (e) {
        this.exportingCSV = false;
        return;
      }
      this.exportingCSV = false;
      if (!items.length) return;
      const headers = [
        "DATE/TIME",
        "BET ID",
        "CUSTOMER",
        "BET TYPE",
        "PROMO",
        "CODE/SPORT",
        "STATE",
        "EVENT",
        "SELECTION",
        "MARKET TYPE",
        "WALLET USED",
        "STAKE",
        "LIABILITY",
        "BET STATUS",
      ];
      const rows = items.map((item) => {
        const betType =
          item.type === "multi"
            ? `Multi (${item.selections ? item.selections.length : 0})`
            : item.type || "";
        const customer = `${item.name || item.username || ""}${
          item.email ? ` (${item.email})` : ""
        }`;
        const promo =
          item.currencyMethod === "bonus"
            ? "bonus"
            : item.promotions && item.promotions.length > 0
            ? "Boost"
            : "";
        const marketType =
          item.type === "single" &&
          item.selections &&
          item.selections.length > 0
            ? item.selections[0].selectionType ||
              item.selections[0].marketType ||
              ""
            : item.type || "";
        const liability = item.odds
          ? formatCurrencyBuiltInFunc(item.stake * item.odds)
          : "-";
        return [
          item.createdTime
            ? format(new Date(item.createdTime), "dd/MM/yyyy HH:mm:ss")
            : "-",
          item.displayId || "-",
          customer,
          betType,
          promo,
          this.convertSportDescriptive(item),
          this.convertState(item) || "-",
          this.convertEvent(item) || "-",
          this.convertSelectionText(item),
          marketType,
          item.currencyMethod || "-",
          item.stake || "-",
          liability,
          item.result || "Unresulted",
        ];
      });
      const csvContent = [headers, ...rows]
        .map((r) =>
          r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")
        )
        .join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `bets-on-race-${format(new Date(), "dd-MM-yyyy")}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    statusVariant(status) {
      switch (status) {
        case "active":
          return "success";
        case "declined":
          return "danger";
        case "pending":
          return "warning";
        case "resulted":
          return "info";
        case "approved":
          return "primary";
        default:
          return "secondary";
      }
    },
    resultVariant(result) {
      switch (result) {
        case "resulted-win":
          return "success";
        case "resulted-loss":
        case "bet-refunded":
          return "danger";
        case "FullyRefunded":
          return "primary";
        case "Void":
          return "secondary";
        case "Draw":
          return "info";
        default:
          return "warning";
      }
    },
    convertState(item) {
      if (!Array.isArray(item.selections)) return "";
      if (item.type === "single") return item.selections[0].stateReport;
      if (item.category === "multi") {
        let text = [];
        item.selections.forEach((element) => {
          if (
            !!element.stateReport &&
            !text.some((i) => i === element.stateReport)
          )
            text.push(element.stateReport);
        });
        return text.join(" - ");
      }
      return item.selections[0].stateReport;
    },
    convertSportDescriptive(item) {
      const codeMap = {
        R: "Thoroughbred",
        G: "Greyhound",
        H: "Harness",
        T: "Thoroughbred",
      };
      const raw = this.convertSport(item);
      return codeMap[raw] || raw || "-";
    },
    convertSport(item) {
      if (!Array.isArray(item.selections)) return "";
      if (item.type === "single") {
        return item.selections[0].sportReport;
      }
      if (item.category === "multi") {
        let text = [];
        item.selections.forEach((element) => {
          if (
            !!element.sportReport &&
            !text.some((i) => i === element.sportReport)
          )
            text.push(element.sportReport);
        });

        return text.join(" - ");
      }
      return item.selections[0].sportReport;
    },
    convertType(item) {
      if (!Array.isArray(item.selections)) return "";

      if (item.category === "multi") {
        let text = [];
        item.selections.forEach((element) => {
          if (
            !!element.categoryReport &&
            !text.some((i) => i === element.categoryReport)
          )
            text.push(element.categoryReport);
        });

        return text.join(" - ");
      }
      return item.selections[0].categoryReport;
    },
    convertEvent(item) {
      if (!Array.isArray(item.selections)) return "";
      if (item.type === "multi") {
        return `Multi (${item.selections.length})`;
      }
      if (item.type === "single") {
        if (item.selections[0].hasOwnProperty("meetingId")) {
          const first = item.selections[0];
          return `${first.raceNumber ? 'Race ' + first.raceNumber : first.eventName} ${first.meetingName ? '- ' + first.meetingName : ''}`;
        }
        if (item.selections[0].hasOwnProperty("eventTitle"))
          return `Event: ${item.selections[0].eventTitle} - (${item.selections[0].marketName})`;
      }
      if (item.type === "srm" || item.category === "exotic")
        return `Race ${item.selections[0].raceNumber} - ${item.selections[0].meetingName}`;

      if (item.type === "quaddie") {
        const selections = [...item.selections].sort((a, b) => {
          if (a.raceNumber !== b.raceNumber) {
            return a.raceNumber - b.raceNumber;
          }
          return a.runnerNo - b.runnerNo;
        });
        return this.formatListRaceNumberQuaddie(selections);
      }
      return `Race ${item.selections[0].raceNumber} - ${item.selections[0].meetingName}`;
    },
    checkBoxedExotic(list) {
      if (!list || list.length <= 1) {
        return true;
      }

      const firstItem = list[0];
      const place1Value = firstItem.place1;
      const place2Value = firstItem.place2;
      const place3Value = firstItem.place3;
      const place4Value = firstItem.place4;

      return list.every((item) => {
        return (
          item.place1 === place1Value &&
          item.place2 === place2Value &&
          item.place3 === place3Value &&
          item.place4 === place4Value
        );
      });
    },
    isExoticBetType(betType) {
      const exoticTypes = ["trifecta", "first4", "quinella", "exacta"];
      return exoticTypes.includes(betType);
    },
    formatListRaceNumberQuaddie(l) {
      let list = [];
      l.forEach((selection) => {
        if (!list.some((i) => i.raceNumber === selection.raceNumber)) {
          list.push(selection);
        }
      });
      return list
        .map((i) => `Race ${i.raceNumber} - ${i.meetingName}`)
        ?.join(", ");
    },
    convertListToGroupedString(list) {
      const group1 = [];
      const group2 = [];
      const group3 = [];
      const group4 = [];

      for (const item of list) {
        const raceNum = item?.runnerNo;

        if (item.place1 === 1) {
          group1.push(raceNum);
        }
        if (item.place2 === 1) {
          group2.push(raceNum);
        }
        if (item.place3 === 1) {
          group3.push(raceNum);
        }
        if (item.place4 === 1) {
          group4.push(raceNum);
        }
      }
      const str1 = group1.sort((a, b) => a - b).join(",");
      const str2 = group2.sort((a, b) => a - b).join(",");
      const str3 = group3.sort((a, b) => a - b).join(",");
      const str4 = group4.sort((a, b) => a - b).join(",");

      const resultParts = [str1, str2, str3, str4].filter(
        (part) => part !== ""
      );

      if (this.checkBoxedExotic(list)) return str1;

      return resultParts.join(" / ");
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
